// src/App.tsx
import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import PostModal from './components/PostModal';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import OnboardingWalkthrough from './components/OnboardingWalkthrough';
import { ArrowUp } from 'lucide-react';
import Pip, { getPipPreference, notifyChromeDark } from './lib/pip';
import { Capacitor } from '@capacitor/core';
import {
  getThemePreference,
  setThemePreference,
  nextThemePreference,
  type ThemePreference,
} from './lib/theme';
import { hasCompletedOnboarding } from './lib/onboarding';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const YouTubePage = React.lazy(() => import('./pages/YouTubePage'));
const InstagramPage = React.lazy(() => import('./pages/InstagramPage'));
const TwitterPage = React.lazy(() => import('./pages/TwitterPage'));
const SoundCloudPage = React.lazy(() => import('./pages/SoundCloudPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const HowToUsePage = React.lazy(() => import('./pages/HowToUsePage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));

export default function App() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    getThemePreference()
  );
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : true
  );
  const isDarkMode =
    themePreference === 'dark' || (themePreference === 'system' && systemDark);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedOnboarding());

  const applyTheme = useCallback((dark: boolean) => {
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    // Keep Android 15 system bar icon contrast in sync with the in-app theme
    notifyChromeDark(dark);
  }, []);

  useEffect(() => {
    applyTheme(isDarkMode);
  }, [isDarkMode, applyTheme]);

  useEffect(() => {
    if (!window.matchMedia) return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);

    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Default ON so leave-app PiP works without hunting Settings first
      Pip.setPipEnabled({ enabled: getPipPreference() }).catch(console.error);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const cycleTheme = () => {
    const next = nextThemePreference(themePreference);
    setThemePreference(next);
    setThemePreferenceState(next);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div
      className={`min-h-screen transition-colors duration-300 bg-pattern font-roboto ${
        isDarkMode ? 'text-white' : 'text-slate-900'
      }`}
    >
      <Navbar
        onPostClick={() => setIsPostModalOpen(true)}
        isDarkMode={isDarkMode}
        themePreference={themePreference}
        onCycleTheme={cycleTheme}
      />

      <PostModal
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
      />

      {showOnboarding && (
        <OnboardingWalkthrough onComplete={() => setShowOnboarding(false)} />
      )}

      <main className={isHome ? 'w-full' : 'pt-24 sm:pt-28 pb-16 px-4 sm:px-6 lg:px-8 w-full'}>
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-lg text-slate-600 dark:text-slate-400">Loading...</p>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about-us" element={<AboutPage />} />
              <Route path="/how-to-use" element={<HowToUsePage />} />
              <Route path="/how-to" element={<HowToUsePage />} />
              <Route path="/guide" element={<HowToUsePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/youtube" element={<YouTubePage />} />
              <Route path="/instagram" element={<InstagramPage />} />
              <Route path="/twitter" element={<TwitterPage />} />
              <Route path="/x" element={<TwitterPage />} />
              <Route path="/soundcloud" element={<SoundCloudPage />} />
              <Route path="/sc" element={<SoundCloudPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-6 right-6 p-4 rounded-full shadow-xl z-50 transition-all ${
            isDarkMode
              ? 'bg-slate-700 hover:bg-slate-600 text-white'
              : 'bg-white hover:bg-gray-100 text-slate-800 shadow-gray-400/30 border border-slate-200'
          }`}
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
      )}
    </div>
  );
}
